import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AccountType, Rights } from '@shared/enums';
import { AccountRights } from '@shared/models';
import { UserDetailsFacade } from '@shared/state/facades';
import { combineLatest, map, Observable } from 'rxjs';

import { canActivateAccountGuard } from './account.guard';
import { canActivateClientGuard } from './client.guard';
import { canActivateAppStatusGuard } from './app-status.guard';
import { CanActivateFn, CanMatchFn } from '@shared/models/guard';

export const canActivateUserAdminGuard: CanActivateFn =
  (): Observable<boolean> => {
    const router = inject(Router);
    const userDetailsFacade = inject(UserDetailsFacade);

    return combineLatest([
      canActivateAccountGuard(),
      canActivateClientGuard(),
      canActivateAppStatusGuard(),
      userDetailsFacade.query.accountType$,
      userDetailsFacade.query.accountRights$,
    ]).pipe(
      map(([hasAccount, hasClient, isAppOnline, accountType, accountRights]) =>
        hasPermissions(
          router,
          hasAccount,
          hasClient,
          isAppOnline,
          accountType,
          accountRights
        )
      )
    );
  };

export const canMatchUserAdminGuard: CanMatchFn = (): Observable<boolean> => {
  const router = inject(Router);
  const userDetailsFacade = inject(UserDetailsFacade);

  return combineLatest([
    canActivateAccountGuard(),
    canActivateClientGuard(),
    canActivateAppStatusGuard(),
    userDetailsFacade.query.accountType$,
    userDetailsFacade.query.accountRights$,
  ]).pipe(
    map(([hasAccount, hasClient, isAppOnline, accountType, accountRights]) =>
      hasPermissions(
        router,
        hasAccount,
        hasClient,
        isAppOnline,
        accountType,
        accountRights
      )
    )
  );
};

const hasPermissions = (
  router: Router,
  hasAccount: boolean,
  hasClient: boolean,
  isAppOnline: boolean,
  accountType: AccountType,
  accountRights: AccountRights
): boolean => {
  if (
    !hasAccount ||
    !hasClient ||
    !isAppOnline ||
    (accountRights.settingsRight === Rights.None &&
      accountType !== AccountType.SystemAdmin)
  ) {
    router.navigate(['/']);

    return false;
  }

  return true;
};
